import styled from '@emotion/styled'
import { colors } from '@mathflat/common/style'
import { animation } from '@design-system/styles'
import clsx from 'clsx'
import React, { useEffect, useMemo, useRef } from 'react'

import type { Props as IconProps } from '../../atoms/Icon/Icon'
import Icon from '../../atoms/Icon/Icon'

type Props = {
  iconName: Extract<IconProps['name'], 'thumb_up' | 'thumb_down'>
  activeColor?: string
  isActive?: boolean
} & React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>

type ThumbProps = Omit<Props, 'iconName'>

const ThumbFactory = ({
  iconName,
  onClick,
  children,
  isActive,
  activeColor = colors.blue.$300,
}: Props) => {
  const initRef = useRef(false)
  const isAnimationAvailable = useMemo(() => initRef.current && isActive, [isActive])

  useEffect(() => {
    // 한 번 이라도 꺼졌다가 켜질 때 애니메이션이 동작하도록 한다. (최초 로드 시 active 상태여도 애니메이션 막음)
    if (!isActive) {
      initRef.current = true
    }
  }, [isActive])

  return (
    <S.Container onClick={onClick} className={clsx(isActive && 'is-active')}>
      <Icon
        name={iconName}
        color={isActive ? activeColor : colors.gray.$500}
        className={clsx('thumb-icon', isAnimationAvailable && 'animatable')}
      />

      <span className="text" style={{ color: isActive ? activeColor : colors.gray.$800 }}>
        {children}
      </span>
    </S.Container>
  )
}

const ThumbUp = ({ children, ...props }: ThumbProps) => {
  return (
    <ThumbFactory iconName="thumb_up" {...props}>
      {children ?? '좋아요'}
    </ThumbFactory>
  )
}

const ThumbDown = ({ children, ...props }: ThumbProps) => {
  return (
    <ThumbFactory iconName="thumb_down" {...props}>
      {children ?? '별로에요'}
    </ThumbFactory>
  )
}

const Thumb = { Up: ThumbUp, Down: ThumbDown }

export default Thumb

const S = {
  Container: styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    .thumb-icon.animatable {
      ${animation.bounceThumb()};
    }
  `,
}
